import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "./api";
import { RefreshReport } from "./RefreshReport";
import type { RefreshJob, WishlistGame } from "./types";

export function WishlistRefresh({ onUpdated, games, onOpenGame, onEditGame }: { onUpdated: () => Promise<void>; games: WishlistGame[]; onOpenGame: (id: number) => void; onEditGame: (id: number) => void }) {
  const [job, setJob] = useState<RefreshJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const retryRunning = useRef(false);
  useEffect(() => {
    let active = true;
    void api.latestRefreshJob().then(value => { if (active) setJob(value); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const jobId = job?.id;
  const status = job?.status;
  useEffect(() => {
    if (!jobId || status !== "running") return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const next = await api.refreshJob(jobId);
        if (!active) return;
        setJob(next); setError(null);
        if (next.status === "running") timer = setTimeout(() => void poll(), 1500);
        else await onUpdated().catch(() => { if (active) setError("Проверка завершена. Не удалось перечитать каталог — обновите страницу."); });
      } catch {
        if (active) setError("Связь с сервером потеряна. Проверка продолжает работать; можно повторно получить прогресс.");
      }
    };
    timer = setTimeout(() => void poll(), 500);
    return () => { active = false; clearTimeout(timer); };
  }, [jobId, status, retry, onUpdated]);
  const start = async () => {
    setStarting(true); setError(null);
    try { setJob(await api.startRefreshJob()); }
    catch { setError("Не удалось получить подтверждение запуска. Обновите прогресс перед повторной попыткой."); }
    finally { setStarting(false); }
  };
  const reconnect = async () => {
    try { setJob(await api.latestRefreshJob()); setRetry(value => value + 1); setError(null); }
    catch { setError("Сервер пока недоступен. Результаты сохраняются на сервере."); }
  };
  const running = job?.status === "running";
  const retryFailure = async (gameId: number) => {
    if (!job || running || retryRunning.current) return;
    retryRunning.current = true; setRetrying(gameId); setRetryError(null);
    try {
      setJob(await api.retryRefreshFailure(job.id, gameId));
      await onUpdated().catch(() => setRetryError("Повтор завершён. Не удалось перечитать каталог — обновите страницу."));
    } catch (error) {
      setRetryError(error instanceof ApiError && error.status === 404 ? "Игра или отчёт больше не доступны. Обновите страницу." : "Не удалось получить результат повтора. Получите свежий прогресс перед следующей попыткой.");
    } finally { retryRunning.current = false; setRetrying(null); }
  };
  const reloadReport = async () => {
    if (!job || retryRunning.current) return;
    try { setJob(await api.refreshJob(job.id)); setRetryError(null); }
    catch { setRetryError("Не удалось перечитать отчёт. Сервер пока недоступен; результаты сохраняются на сервере."); }
  };
  return <div className="refresh-control">
    <button type="button" disabled={starting || running || retrying !== null} onClick={() => void start()}>{running ? `Проверено ${job.completed} / ${job.total}` : starting ? "Запускаем…" : "Обновить все цены"}</button>
    {job ? <button type="button" className={job.failed_games ? "refresh-report-trigger has-errors" : "refresh-report-trigger"} onClick={() => setReportOpen(true)}>{job.failed_games ? `Ошибки: ${job.failed_games}` : job.status === "interrupted" ? "Прервано · отчёт" : "Отчёт обновления"}</button> : null}
    {error ? <div className="refresh-connection-error"><p role="alert">{error}</p><button type="button" onClick={() => void reconnect()}>Получить прогресс</button></div> : null}
    {reportOpen && job ? <RefreshReport job={job} games={games} retrying={retrying} error={retryError} onRetry={id => void retryFailure(id)} onReload={() => void reloadReport()} onClose={() => setReportOpen(false)} onOpenGame={id => { setReportOpen(false); onOpenGame(id); }} onEditGame={id => { setReportOpen(false); onEditGame(id); }} /> : null}
  </div>;
}
